import { Location, useLocation } from 'react-router-dom';
import { AiChatWindowTarget } from '@/state/aiChatWindowState';

/**
 * 폰의 AI 시트 주소 규칙 — 설계 docs/ai-chat/share-design.md 1장, impl-3a.md 1장.
 *
 * 폰에서 `/ai`·`/ai/:id`·`/ai/history`·`/ai/s/:token` 은 페이지가 아니라 **지금 화면 위에 올라오는 시트**다.
 * 시트를 열 때 주소를 한 칸 쌓으면서(push) 그 아래 깔린 화면의 주소를 `state.aiBackground` 에 담는다.
 * - 본문 라우트(App.tsx)는 `aiBackground` 가 있으면 그 주소의 화면을 그대로 그린다 → 뒤 화면이 유지된다.
 * - 시트(AiChatSheet)는 실제 주소를 보고 대화·목록·공유 화면을 고른다.
 * - 시트 안에서 화면을 바꿀 때는 주소를 **바꿔치기(replace)** 한다 — 시트는 늘 기록 한 칸이라, 뒤로 가기 한 번이 곧 시트 닫기다.
 * - 주소로 바로 들어오면(공유·알림 링크, 새로고침 전 기록 없음) 지금 칸을 홈으로 바꾸고 그 위에 시트 칸을 쌓는다.
 *   그래서 기록이 없어도 뒤로·끌기·⌄ 는 시트만 닫고 홈에 남는다.
 */

/** 시트 칸의 state 에 아래 화면 주소를 담는 키 */
const BACKGROUND_KEY: string = 'aiBackground';
/** 홈 말풍선 질문을 눌러 열 때 바로 보낼 질문(state) */
const AUTO_QUESTION_KEY: string = 'aiAutoQuestion';
/** 그 질문을 보내지 않고 질문 칸에 채워 두기만 할지(홈 개인화 말풍선 — 질문 수 차감 방지) */
const AUTO_FILL_KEY: string = 'aiAutoFill';

export type AiSheetBackground = Pick<Location, 'pathname' | 'search' | 'hash' | 'state' | 'key'>;

/** 주소로 바로 들어왔을 때 시트 아래에 까는 홈 */
const HOME_BACKGROUND: AiSheetBackground = { pathname: '/', search: '', hash: '', state: null, key: 'default' };

const normalize = (pathname: string): string => (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname);

const decodeSegment = (value: string): string | null => {
    try {
        const decoded: string = decodeURIComponent(value);
        return decoded.trim() ? decoded : null;
    } catch {
        return null;
    }
};

/** AI 시트 주소면 무엇을 보여 줄지, 아니면 null */
export const parseAiSheetPath = (pathname: string): AiChatWindowTarget | null => {
    const path: string = normalize(pathname);
    if (path === '/ai') return { view: 'chat', chatId: null };
    if (path === '/ai/history') return { view: 'history', chatId: null };
    const share: RegExpMatchArray | null = path.match(/^\/ai\/s\/([^/]+)$/);
    if (share) {
        const token: string | null = decodeSegment(share[1]);
        return token ? { view: 'share', chatId: null, shareToken: token } : null;
    }
    const chat: RegExpMatchArray | null = path.match(/^\/ai\/([^/]+)$/);
    if (chat) {
        const chatId: string | null = decodeSegment(chat[1]);
        return chatId ? { view: 'chat', chatId } : null;
    }
    return null;
};

/** 시트 상태에 해당하는 주소 */
export const aiSheetPathOf = (target: AiChatWindowTarget | null): string => {
    if (!target) return '/ai';
    if (target.view === 'history') return '/ai/history';
    if (target.view === 'share' && target.shareToken) return `/ai/s/${encodeURIComponent(target.shareToken)}`;
    return target.chatId ? `/ai/${encodeURIComponent(target.chatId)}` : '/ai';
};

/** 이 칸이 시트 칸이면 아래 깔린 화면의 주소 */
export const readAiBackground = (state: unknown): AiSheetBackground | null => {
    if (!state || typeof state !== 'object') return null;
    const value: unknown = (state as Record<string, unknown>)[BACKGROUND_KEY];
    if (!value || typeof value !== 'object') return null;
    const { pathname, search, hash, key } = value as Partial<AiSheetBackground>;
    if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
    return {
        pathname,
        search: typeof search === 'string' ? search : '',
        hash: typeof hash === 'string' ? hash : '',
        state: (value as AiSheetBackground).state ?? null,
        key: typeof key === 'string' ? key : 'default',
    };
};

/** 시트 칸에 넣을 state — 아래 화면 주소(+ 바로 보낼 질문) */
export const aiSheetState = (background: AiSheetBackground | null, autoQuestion: string | null = null, fill: boolean = false): Record<string, unknown> => ({
    [BACKGROUND_KEY]: background ?? HOME_BACKGROUND,
    ...(autoQuestion ? { [AUTO_QUESTION_KEY]: autoQuestion } : {}),
    ...(autoQuestion && fill ? { [AUTO_FILL_KEY]: true } : {}),
});

/** 지금 화면(시트를 열 화면)을 아래 화면 주소로 — 그 화면 자신이 시트 칸이면(시트 안에서 다시 열기) 그 아래 화면 */
export const aiBackgroundOf = (location: AiSheetBackground): AiSheetBackground => {
    const existing: AiSheetBackground | null = readAiBackground(location.state);
    if (existing) return existing;
    return { pathname: location.pathname, search: location.search, hash: location.hash, state: location.state ?? null, key: location.key };
};

/** 시트 칸 state 에서 바로 보낼 질문 */
export const readAiAutoQuestion = (state: unknown): string | null => {
    const text: unknown = state && typeof state === 'object' ? (state as Record<string, unknown>)[AUTO_QUESTION_KEY] : null;
    return typeof text === 'string' && text.trim() ? text : null;
};

/** 시트 칸 state 의 질문을 보내지 않고 칸에 채우기만 하는지 */
export const readAiAutoFill = (state: unknown): boolean =>
    !!state && typeof state === 'object' && (state as Record<string, unknown>)[AUTO_FILL_KEY] === true;

/** 같은 화면(주소·검색어)인지 — 시트를 닫고 아래 화면으로 돌아왔는지 가른다 */
export const isSamePlace = (a: Pick<Location, 'pathname' | 'search'>, b: Pick<Location, 'pathname' | 'search'>): boolean =>
    normalize(a.pathname) === normalize(b.pathname) && a.search === b.search;

/**
 * 본문·머리줄이 그릴 주소 — 시트 칸이면 아래 깔린 화면의 주소, 아니면 지금 주소.
 * 앱 머리줄·하단 탭·스크롤 맨 위로 같은 전역 부품이 시트가 열려도 뒤 화면 기준으로 움직이게 한다.
 */
export const useAiBackgroundLocation = (): Location => {
    const location: Location = useLocation();
    const background: AiSheetBackground | null = readAiBackground(location.state);
    return background ? { ...location, ...background } : location;
};
